<template lang="pug">
md-layout(md-flex='100')
  md-layout(md-flex='90', md-flex-offset='5')
    md-card.md-table-card.content-table
      md-card-header
        .header-row
          h2.md-title Client Financial Summary
          .header-right
            .period-control
              span.period-label-text Period:
              md-button.md-icon-button(@click='prevPeriod', :disabled='loading')
                md-icon(md-iconset='fa fa-chevron-left')
              md-menu(md-direction='bottom-end', md-size='auto')
                md-button.md-icon-button(md-menu-trigger)
                  md-icon(md-iconset='fa fa-calendar-alt')
                md-menu-content
                  .period-picker-popup
                    md-input-container
                      label Month
                      md-select(v-model='filters.month', @selected='onPeriodChanged')
                        md-option(:value='m.value', v-for='m in months', :key='m.value') {{ m.name }}
                    md-input-container
                      label Year
                      md-select(v-model='filters.year', @selected='onPeriodChanged')
                        md-option(:value='y.value', v-for='y in years', :key='y.value') {{ y.value }}
              span.current-period-label {{ currentPeriodLabel }}
              md-button.md-icon-button(@click='nextPeriod', :disabled='loading')
                md-icon(md-iconset='fa fa-chevron-right')

      stitch-loader(:loading='loading', :size='45')
        .no-data(v-if='!loading && clients.length === 0')
          md-icon.md-accent(md-iconset='fa fa-exclamation-circle')
          span No financial data found for the selected period.

        div(v-if='clients.length > 0')
          .table
            .titleLine.styleRow
              p.rowElements.col-expand
              p.rowElements.col-client Client
              p.rowElements.col-money Platform Fee
              p.rowElements.col-money Videos Purchased
              p.rowElements.col-money Videos Sold Net Revenue
              p.rowElements.col-money Rentals Net Revenue
              p.rowElements.col-money Total
              p.rowElements.col-actions Actions


            template(v-for='client in clients')
              .styleRow.colorHover(@click='toggleExpand(client.accountId)', :key='client.accountId')
                div.rowElements.col-expand
                  md-icon.expand-icon(:md-iconset="isExpanded(client.accountId) ? 'fa fa-chevron-down' : 'fa fa-chevron-right'")
                div.rowElements.col-client {{ client.accountName }}
                div.rowElements.col-money {{ formatCurrency(client.platformFee) }}
                div.rowElements.col-money {{ formatCurrency(client.videosPurchased.total) }}
                div.rowElements.col-money {{ formatCurrency(client.videosSold.total) }}
                div.rowElements.col-money.text-green {{ formatCurrency(client.rentals.net) }}
                div.rowElements.col-money.text-bold {{ formatCurrency(client.grandTotal) }}
                div.rowElements.col-actions
                  md-button.md-icon-button.md-raised.md-primary(@click.stop='openFeeConfig(client)')
                    md-icon(md-iconset='fa fa-cog')

              .detail-row(v-if='isExpanded(client.accountId)', :key="client.accountId + '-detail'")
                .detail-content

                  //- Videos Sold sub-table
                  h4 Videos Sold
                  div(v-if='client.videosSold.details && client.videosSold.details.length > 0')
                    .sub-table
                      .sub-titleLine.sub-styleRow
                        p.sub-rowElements Buyer
                        p.sub-rowElements.col-money Videos Sold
                        p.sub-rowElements.col-money Rhebus Fee
                        p.sub-rowElements.col-money.highlight-yellow Videos Sold (net)
                      .sub-styleRow(v-for='(d, idx) in client.videosSold.details', :key="'sold-' + idx")
                        div.sub-rowElements {{ d.buyer }}
                        div.sub-rowElements.col-money {{ formatCurrency(d.videosSold) }}
                        div.sub-rowElements.col-money {{ formatCurrency(d.rhebusFee) }}
                        div.sub-rowElements.col-money.highlight-yellow {{ formatCurrency(d.videosSoldNet) }}
                      .sub-styleRow.sub-totals-row
                        div.sub-rowElements(style='font-weight:600') Total
                        div.sub-rowElements.col-money {{ formatCurrency(client.videosSold.gross) }}
                        div.sub-rowElements.col-money {{ formatCurrency(client.videosSold.rhebusFee) }}
                        div.sub-rowElements.col-money.highlight-yellow {{ formatCurrency(client.videosSold.total) }}
                  .no-sub-data(v-else) No videos sold this period.

                  //- Videos Purchased sub-table
                  h4 Videos Purchased
                  div(v-if='client.videosPurchased.details && client.videosPurchased.details.length > 0')
                    .sub-table
                      .sub-titleLine.sub-styleRow
                        p.sub-rowElements Seller
                        p.sub-rowElements.col-money Videos Bought
                        p.sub-rowElements.col-money Rhebus Fee
                        p.sub-rowElements.col-money.highlight-blue Rhebus Purchases Out
                      .sub-styleRow(v-for='(d, idx) in client.videosPurchased.details', :key="'bought-' + idx")
                        div.sub-rowElements {{ d.seller }}
                        div.sub-rowElements.col-money {{ formatCurrency(d.videosBought) }}
                        div.sub-rowElements.col-money {{ formatCurrency(d.rhebusFee) }}
                        div.sub-rowElements.col-money.highlight-blue {{ formatCurrency(d.purchasesOut) }}
                      .sub-styleRow.sub-totals-row
                        div.sub-rowElements(style='font-weight:600') Total
                        div.sub-rowElements.col-money {{ formatCurrency(client.videosPurchased.total) }}
                        div.sub-rowElements.col-money {{ formatCurrency(client.videosPurchased.totalRhebusFee) }}
                        div.sub-rowElements.col-money.highlight-blue {{ formatCurrency(client.videosPurchased.totalRhebusPurchasesOut) }}
                  .no-sub-data(v-else) No videos purchased this period.

                  //- Rentals Out sub-table
                  h4 Video Rentals Out (gross)
                  div(v-if='client.rentals.gross > 0')
                    .sub-table
                      .sub-titleLine.sub-styleRow
                        p.sub-rowElements.col-money Video Rentals Out (gross)
                        p.sub-rowElements.col-money Roku Fees
                        p.sub-rowElements.col-money Stripe Fees
                        p.sub-rowElements.col-money Rhebus Fee
                        p.sub-rowElements.col-money.highlight-green Video Rentals Out (net)
                      .sub-styleRow
                        div.sub-rowElements.col-money(style='font-weight:600') {{ formatCurrency(client.rentals.gross) }}
                        div.sub-rowElements.col-money(style='font-weight:600') {{ formatCurrency(client.rentals.rokuFees) }}
                        div.sub-rowElements.col-money(style='font-weight:600') {{ formatCurrency(client.rentals.stripeFees) }}
                        div.sub-rowElements.col-money(style='font-weight:600') {{ formatCurrency(client.rentals.rhebusCommission) }}
                        div.sub-rowElements.col-money.highlight-green(style='font-weight:600') {{ formatCurrency(client.rentals.net) }}
                  .no-sub-data(v-else) No rentals this period.

            //- Totals Row
            .styleRow.totals-row
              div.rowElements.col-expand
              div.rowElements.col-client(style='font-weight:600') TOTALS
              div.rowElements.col-money {{ formatCurrency(totals.platformFee) }}
              div.rowElements.col-money {{ formatCurrency(totals.videosPurchased) }}
              div.rowElements.col-money {{ formatCurrency(totals.videosSoldNet) }}
              div.rowElements.col-money.text-green {{ formatCurrency(totals.rentalsNet) }}
              div.rowElements.col-money.text-bold {{ formatCurrency(totals.grandTotal) }}
              div.rowElements.col-actions
            md-table-pagination(
              :md-size='pagination.size',
              :md-total='pagination.total',
              :md-page='pagination.page',
              md-label='Rows',
              md-separator='of',
              :md-page-options='[10, 25, 50]',
              @pagination='onPageChanged'
            )

    //- Fee Config Dialog
    md-dialog(ref='feeConfigDialog')
      md-dialog-title Fee Configuration &mdash; {{ selectedClient ? selectedClient.accountName : '' }}
      md-dialog-content
        md-input-container
          label Platform Fee ($)
          md-input(v-model.number='feeForm.platformFee', type='number', min='0')
        md-input-container
          label Rental Fee Rate (%)
          md-input(v-model.number='feeForm.rentalFeeRate', type='number', min='0', max='100')
        md-input-container
          label License Fee Rate (%)
          md-input(v-model.number='feeForm.licenseFeeRate', type='number', min='0', max='100')
      md-dialog-actions
        md-button(@click='closeFeeConfig') Cancel
        md-button.md-raised.save-btn(@click='saveFeeConfig', :disabled='savingFeeConfig') {{ savingFeeConfig ? 'Saving...' : 'Save' }}
</template>

<script>
import moment from 'moment';
import stitchLoader from '../Loader';
import reportService from '../../services/reportService';
import { generateMonths, generateYears } from './dateUtils';

export default {
  components: { stitchLoader },
  data() {
    const now = new Date();
    return {
      months: generateMonths(),
      years: generateYears(),
      filters: {
        month: now.getMonth() + 1,
        year: now.getFullYear()
      },
      loading: false,
      clients: [],
      totals: {},
      pagination: { page: 1, size: 10, total: 0 },
      expandedAccounts: {},
      selectedClient: null,
      feeForm: { platformFee: 0, rentalFeeRate: 30, licenseFeeRate: 30 },
      savingFeeConfig: false,
      generatingAccountId: null
    };
  },
  computed: {
    currentPeriodLabel() {
      return moment().month(this.filters.month - 1).year(this.filters.year).format('MMMM YYYY');
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await reportService.getFinancialSummary({
          year: this.filters.year,
          month: this.filters.month,
          page: this.pagination.page,
          pageSize: this.pagination.size
        });
        this.clients = data.clients || [];
        this.totals = data.totals || {};
        this.pagination.total = data.pagination ? data.pagination.totalCount : this.clients.length;
      } catch (err) {
        console.error('Failed to fetch financial summary', err);
      } finally {
        this.loading = false;
      }
    },
    prevPeriod() {
      let { month, year } = this.filters;
      month -= 1;
      if (month < 1) { month = 12; year -= 1; }
      this.filters.month = month;
      this.filters.year = year;
      this.pagination.page = 1;
      this.fetchData();
    },
    nextPeriod() {
      let { month, year } = this.filters;
      month += 1;
      if (month > 12) { month = 1; year += 1; }
      this.filters.month = month;
      this.filters.year = year;
      this.pagination.page = 1;
      this.fetchData();
    },
    onPeriodChanged() {
      this.pagination.page = 1;
      this.fetchData();
    },
    onPageChanged(paginationParams) {
      this.pagination.page = paginationParams.page;
      this.pagination.size = paginationParams.size;
      this.fetchData();
    },
    toggleExpand(accountId) {
      this.$set(this.expandedAccounts, accountId, !this.expandedAccounts[accountId]);
    },
    isExpanded(accountId) {
      return !!this.expandedAccounts[accountId];
    },
    formatCurrency(value) {
      const n = parseFloat(value) || 0;
      return `$${n.toFixed(2)}`;
    },
    openFeeConfig(client) {
      this.selectedClient = client;
      this.feeForm = {
        platformFee: client.feeConfig.platformFee || 0,
        rentalFeeRate: (client.feeConfig.rentalFeeRate || 0) * 100,
        licenseFeeRate: (client.feeConfig.licenseFeeRate || 0) * 100
      };
      this.$refs.feeConfigDialog.open();
    },
    closeFeeConfig() {
      this.$refs.feeConfigDialog.close();
    },
    async saveFeeConfig() {
      this.savingFeeConfig = true;
      try {
        await reportService.updateAccountFeeConfig(this.selectedClient.accountId, {
          platformFee: this.feeForm.platformFee,
          rentalFeeRate: this.feeForm.rentalFeeRate / 100,
          licenseFeeRate: this.feeForm.licenseFeeRate / 100
        });
        this.closeFeeConfig();
        this.fetchData();
      } catch (err) {
        console.error('Failed to save fee config', err);
      } finally {
        this.savingFeeConfig = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>

.period-control {
  .fa-chevron-left,
  .fa-chevron-right,
  .fa-calendar-alt {
    color: #fff !important;
    font-size: 14px !important;
    position: relative !important;
    top: 4px !important;
  }
}

.fa-cog {
  color: #fff !important;
  font-size: 14px;
  position: relative !important;
  top: 4px !important;
}

.header-row,
.header-right,
.period-control,
.styleRow,
.no-data {
  display: flex;
  align-items: center;
}

.content-table {
  width: 100%;
  margin-top: 20px;
  padding: 10px;
  margin: 0;
  border-radius: 0;
  box-shadow: none;
}
.header-row {
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 8px;
}
.header-right {
  gap: 8px;
  flex-wrap: wrap;
}
.period-control {
  gap: 2px;
}
.period-label-text {
  font-weight: 500;
  margin-right: 4px;
}
.current-period-label {
  font-weight: 600;
  white-space: nowrap;
  margin: 0 4px;
}
.period-picker-popup {
  padding: 16px;
  min-width: 100px;
}

.table {
  margin-top: 8px;
}
.titleLine {
  border-bottom: 1px solid rgba(153, 155, 155, 0.486);
  padding: 4px 0;
}
.styleRow {
  cursor: pointer;
  p {
    font-weight: 600;
  }
}
.rowElements {
  padding: 6px 8px;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-expand {
  flex: 0 0 40px;
  text-align: center;
}
.col-client {
  flex: 2;
}
.col-money {
  flex: 1;
  text-align: right;
}
.col-actions {
  flex: 0 0 80px;
  text-align: center;
}
.colorHover:hover {
  background-color: #eee;
}

.expand-icon {
  font-size: 12px !important;
}

.text-bold {
  font-weight: 700;
}
.text-green {
  color: #059669;
}

// Detail row
.detail-row {
  border-bottom: 1px solid #e0e0e0;
}
.detail-content {
  padding: 16px 40px;
  background: #f9fafb;
  h4 {
    font-size: 14px;
    font-weight: bold;
    margin: 16px 0 8px;
    &:first-child { margin-top: 0; }
  }
}

// Sub-tables
.sub-table {
  margin-bottom: 8px;
}
.sub-styleRow {
  display: flex;
  align-items: center;
  p {
    font-weight: 600;
  }
}
.sub-titleLine {
  border-bottom: 1px solid rgba(153, 155, 155, 0.3);
  padding: 2px 0;
}
.sub-rowElements {
  flex: 1;
  padding: 4px 8px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sub-totals-row {
  border-top: 1px solid #ccc;
  background: #f0f0f0;
  font-weight: 600;
}

.highlight-yellow {
  background: #fefce8;
}
.highlight-blue {
  background: #eff6ff;
}
.highlight-green {
  background: #f0fdf4;
}

.totals-row {
  display: flex;
  align-items: center;
  border-top: 2px solid #ccc;
  background: #eef2ff;
  font-weight: 600;
  cursor: default;
}

.no-data {
  gap: 8px;
  padding: 20px;
  color: #666;
  font-size: 14px;
}
.no-sub-data {
  padding: 8px;
  color: #999;
  font-size: 12px;
  font-style: italic;
}
// Save button blue with white text
.save-btn {
  background-color: #4ea9dd !important;
  color: #fff !important;
}

// Smaller buttons and icons
.md-icon-button {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
}
</style>



// WEBPACK FOOTER //
// src/shared-components/report/FinancialSummaryTable.vue